import React, { ReactElement, useCallback, useMemo, useState } from "react";
import type { DepositWithdrawCollaterals } from "../../constants/addresses";

type ConnectWalletFlow = "default" | "deposit";

interface IConnectWalletContextType {
  yieldVaultToggle: boolean;
  setYieldVaultToggle: (yes: boolean) => void;
  showConnectModal: boolean;
  setShowConnectModal: (show: boolean) => void;
  /**
   * Bumps on every request to SHOW the connect flow, even if it is already
   * showing. SDK chains hide our frame behind Para/Privy; after the user closes
   * that dialog `showConnectModal` can stay true until the in-flight connect
   * settles, so `setShowConnectModal(true)` is a no-op React update. Consumers
   * re-open the SDK modal when this counter changes.
   */
  connectIntent: number;
  connectWalletFlow: ConnectWalletFlow;
  depositAsset?: DepositWithdrawCollaterals;
  openDepositFlow: (asset?: DepositWithdrawCollaterals) => void;
}

interface IConnectWalletContextProviderProps {
  children: ReactElement;
}

export const ConnectWalletContext =
  React.createContext<IConnectWalletContextType>({
    yieldVaultToggle: false,
    setYieldVaultToggle: () => {},
    showConnectModal: false,
    setShowConnectModal: () => {},
    connectIntent: 0,
    connectWalletFlow: "default",
    depositAsset: undefined,
    openDepositFlow: () => {},
  });

export function ConnectWalletContextProvider({
  children,
}: IConnectWalletContextProviderProps) {
  const [yieldVaultToggle, setYieldVaultToggle] = useState(false);
  const [showConnectModal, setShowConnectModalState] = useState(false);
  const [connectIntent, setConnectIntent] = useState(0);
  const [connectWalletFlow, setConnectWalletFlow] =
    useState<ConnectWalletFlow>("default");
  const [depositAsset, setDepositAsset] = useState<DepositWithdrawCollaterals>();

  const setShowConnectModal = useCallback((show: boolean) => {
    if (!show) {
      setConnectWalletFlow("default");
      setDepositAsset(undefined);
    } else {
      setConnectWalletFlow("default");
      setConnectIntent((n) => n + 1);
    }
    setShowConnectModalState(show);
  }, []);

  const openDepositFlow = useCallback((asset?: DepositWithdrawCollaterals) => {
    setConnectWalletFlow("deposit");
    setDepositAsset(asset);
    setConnectIntent((n) => n + 1);
    setShowConnectModalState(true);
  }, []);

  const value = useMemo(
    () => ({
      yieldVaultToggle,
      setYieldVaultToggle,
      showConnectModal,
      setShowConnectModal,
      connectIntent,
      connectWalletFlow,
      depositAsset,
      openDepositFlow,
    }),
    [
      connectIntent,
      connectWalletFlow,
      depositAsset,
      openDepositFlow,
      setShowConnectModal,
      showConnectModal,
      yieldVaultToggle,
    ]
  );

  return (
    <ConnectWalletContext.Provider value={value}>
      {children}
    </ConnectWalletContext.Provider>
  );
}
